'use client';

import type { ReactNode } from 'react';
import { useEffect, useEffectEvent, useId, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { useFocusTrap } from '@mantine/hooks';

import { Box, Button, Group, Stack, Text } from '@highlight-cp/core-ui';

import classes from './confirm-banner.module.css';

/** Mantine's modals leave an Escape alone when it lands on an element marked with this. */
const KEEPS_ESCAPE = { 'data-mantine-stop-propagation': true };

interface ConfirmBannerProps {
  title: string;
  children: ReactNode;
  onConfirm: () => void;
  onCancel: () => void;
  /** It went unanswered, with the modal that held it. */
  onGone: () => void;
}

/**
 * The band a {@link useConfirmDeleteBanner} ask shows across the top of a modal, which holds the
 * rest of itself inert until it's answered. An alert dialog of its own: focus starts on Cancel, the
 * safe answer to a destructive question, and stays between its two buttons, since Mantine's trap
 * around the modal doesn't skip inert controls. Escape answers Cancel too, rather than closing the
 * modal.
 */
export default function ConfirmBanner({
  title,
  children,
  onConfirm,
  onCancel,
  onGone,
}: ConfirmBannerProps) {
  const { t } = useTranslation();
  const trapRef = useFocusTrap();
  const isMounted = useRef(false);
  const titleId = useId();
  const messageId = useId();
  const gone = useEffectEvent(onGone);

  // Checked a tick later, since development's strict mode unmounts and remounts every effect once.
  useEffect(() => {
    isMounted.current = true;
    return () => {
      isMounted.current = false;
      setTimeout(() => isMounted.current || gone());
    };
  }, []);

  return (
    <Box className={classes.band}>
      <Box className={classes.clip}>
        <Group
          aria-describedby={messageId}
          aria-labelledby={titleId}
          bg="var(--mantine-color-red-light)"
          className={classes.ask}
          gap="sm"
          onKeyDown={(event) =>
            event.key === 'Escape' && !event.nativeEvent.isComposing && onCancel()
          }
          py="sm"
          ref={trapRef}
          role="alertdialog"
        >
          <Stack flex="1 1 16rem" gap={0} miw={0}>
            <Text fw={600} id={titleId} size="sm">
              {title}
            </Text>
            <Text id={messageId} size="xs">
              {children}
            </Text>
          </Stack>
          <Group gap="xs" wrap="nowrap">
            <Button data-autofocus onClick={onCancel} variant="default" {...KEEPS_ESCAPE}>
              {t('Cancel')}
            </Button>
            <Button color="red" onClick={onConfirm} {...KEEPS_ESCAPE}>
              {t('Delete')}
            </Button>
          </Group>
        </Group>
      </Box>
    </Box>
  );
}
